import React, { useState, useEffect } from 'react';
import { StudentSummary, StudentProgressRecord, DetailedStudentAnalytics, StudentReport, Badge } from '../../types';
import LoadingSpinner from '../ui/LoadingSpinner';
import KeyboardHeatmap from '../ui/KeyboardHeatmap';
import GameStatsGrid from '../ui/GameStatsGrid';
import ActivityChart from '../ui/ActivityChart';
import BadgeIcon from '../ui/BadgeIcon';
import { api } from '../../utils/api';

interface StudentDetailModalProps {
  student: StudentSummary;
  onClose: () => void;
}

const StatPill: React.FC<{ label: string; value: string; icon: string }> = ({ label, value, icon }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
    <div className="text-2xl mb-1">{icon}</div>
    <div className="text-lg font-bold text-blue-600">{value}</div>
    <div className="text-xs uppercase text-gray-500 tracking-wide">{label}</div>
  </div>
);

const StudentDetailModal: React.FC<StudentDetailModalProps> = ({ student, onClose }) => {
  const [progress, setProgress] = useState<StudentProgressRecord[]>([]);
  const [analytics, setAnalytics] = useState<DetailedStudentAnalytics | null>(null);
  const [report, setReport] = useState<StudentReport | null>(null);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editData, setEditData] = useState<{ [key: string]: { grade: string; feedback: string } }>({});

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const [progressData, analyticsData, reportData, badgeData] = await Promise.all([
          api.get<StudentProgressRecord[]>(`/api/v1/student/${student.user_id}/progress`),
          api.get<DetailedStudentAnalytics>(`/api/v1/student/${student.user_id}/analytics`),
          api.get<StudentReport>(`/api/v1/student/${student.user_id}/report`),
          api.get<Badge[]>(`/api/v1/student/${student.user_id}/badges`),
        ]);

        setProgress(progressData);
        setAnalytics(analyticsData);
        setReport(reportData);
        setBadges(badgeData);

        const initialData: { [key: string]: { grade: string; feedback: string } } = {};
        progressData.forEach(p => {
          initialData[p.progress_id] = {
            grade: p.teacher_grade || '',
            feedback: p.teacher_feedback || ''
          };
        });
        setEditData(initialData);
      } catch (e) {
        if (e instanceof Error) setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [student.user_id]);

  const handleEditChange = (progressId: string, field: 'grade' | 'feedback', value: string) => {
    setEditData(prev => ({ ...prev, [progressId]: { ...prev[progressId], [field]: value } }));
  };

  const handleSaveGrade = async (progressId: string) => {
    const dataToSave = editData[progressId];
    try {
      const updatedRecord = await api.put<StudentProgressRecord>(`/api/v1/progress/${progressId}/grade`, {
        teacher_grade: dataToSave.grade,
        teacher_feedback: dataToSave.feedback
      });
      setProgress(prev => prev.map(p => p.progress_id === progressId ? updatedRecord : p));
    } catch (e) {
      console.error("Error saving grade:", e);
    }
  };

  const tutorialPct = report && report.tutorial_total > 0 ? Math.round((report.tutorial_completed / report.tutorial_total) * 100) : 0;
  const lastActiveLabel = report?.last_active
    ? new Date(`${report.last_active}T00:00:00Z`).toLocaleDateString()
    : 'Never';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 border-b pb-4">
          <h2 className="text-2xl font-bold text-gray-800">Student Report: {student.first_name} {student.last_name}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-3xl">&times;</button>
        </div>
        <div className="overflow-y-auto space-y-6 pr-1">
          {loading && <LoadingSpinner message="Loading student report..." />}
          {error && <div className="text-red-500">{error}</div>}
          {!loading && !error && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatPill label="Curriculum" value={`${tutorialPct}%`} icon="📘" />
                <StatPill label="Current Streak" value={`${report?.current_streak ?? 0}d`} icon="🔥" />
                <StatPill label="Badges" value={String(badges.length)} icon="🏅" />
                <StatPill label="Last Active" value={lastActiveLabel} icon="🕒" />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <h3 className="text-md font-semibold text-gray-700">Curriculum Progress</h3>
                  <span className="text-xs text-gray-500">{report?.tutorial_completed ?? 0} / {report?.tutorial_total ?? 0} lessons</span>
                </div>
                <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-500" style={{ width: `${tutorialPct}%` }} />
                </div>
              </div>

              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-2">Activity (Last 14 Days)</h3>
                {report && <ActivityChart activity={report.activity} />}
              </div>

              {analytics && analytics.problem_keys && Object.keys(analytics.problem_keys).length > 0 && (
                <KeyboardHeatmap data={analytics.problem_keys} />
              )}

              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-2">Games</h3>
                {report && <GameStatsGrid games={report.games} rpgLevel={report.rpg_level} />}
              </div>

              {badges.length > 0 && (
                <div>
                  <h3 className="text-md font-semibold text-gray-700 mb-2">Badges Earned</h3>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {badges.map(badge => (
                      <div key={badge.badge_name} className="flex flex-col items-center p-2 bg-gray-50 rounded-lg border border-gray-200" title={badge.description}>
                        <div className="w-12 h-12"><BadgeIcon iconName={badge.icon_name} /></div>
                        <span className="text-xs text-gray-600 text-center mt-1">{badge.badge_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-2">Homework &amp; Custom Lessons</h3>
                <div className="space-y-4">
                  {progress.length > 0 ? progress.map(p => (
                    <div key={p.progress_id} className="bg-gray-50 p-4 rounded-lg shadow-sm">
                      <h3 className="font-semibold text-lg text-gray-900">{p.title}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                        <span className="text-sm">Auto-Score: <b className="text-blue-600">{Number(p.wpm).toFixed(0)} WPM</b> at <b className="text-blue-600">{Number(p.accuracy).toFixed(0)}%</b></span>
                        <span className="text-sm">Completed: {new Date(p.completed_at).toLocaleString()}</span>
                      </div>
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-1">
                          <label htmlFor={`grade-${p.progress_id}`} className="block text-sm font-medium text-gray-700">Teacher Grade</label>
                          <input type="text" id={`grade-${p.progress_id}`} value={editData[p.progress_id]?.grade || ''} onChange={(e) => handleEditChange(p.progress_id, 'grade', e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" placeholder="e.g., A+" />
                        </div>
                        <div className="md:col-span-2">
                          <label htmlFor={`feedback-${p.progress_id}`} className="block text-sm font-medium text-gray-700">Teacher Feedback</label>
                          <input type="text" id={`feedback-${p.progress_id}`} value={editData[p.progress_id]?.feedback || ''} onChange={(e) => handleEditChange(p.progress_id, 'feedback', e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" placeholder="e.g., Great work!" />
                        </div>
                      </div>
                      <div className="text-right mt-2"><button onClick={() => handleSaveGrade(p.progress_id)} className="px-4 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">Save Grade</button></div>
                    </div>
                  )) : <p className="text-gray-500">This student has not completed any homework or custom lessons yet.</p>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDetailModal;
